import React, { useState, useMemo } from 'react';
import { investigationService } from '../services/api';
import '../styles/ClusteringPanel.css';

/**
 * ClusteringPanel — Address Clustering (multi-heuristic entity grouping)
 *
 * Calls GET /investigations/:id/clusters (modules/address_clustering.py) on
 * demand. Runs three heuristics over the investigation's graph data:
 *   - common_input_ownership   (HIGH confidence, Bitcoin only — fetches raw
 *                                tx detail from Blockstream)
 *   - shared_funding_source    (MEDIUM confidence, any chain — derived from
 *                                stored graph data, no extra fetches)
 *   - graph_connectivity       (LOW confidence, any chain — baseline/context)
 *
 * This is a "run on demand" panel (not auto-computed like PatternPanel)
 * because the Bitcoin heuristic makes external HTTP calls and can take a
 * moment on larger investigations.
 *
 * Props:
 *   investigationId — id of the current investigation
 *   investigation   — investigation object (used for .blockchain)
 */

const CONFIDENCE_META = {
  high:   { label: 'HIGH',   cls: 'cl-conf-high' },
  medium: { label: 'MEDIUM', cls: 'cl-conf-medium' },
  low:    { label: 'LOW',    cls: 'cl-conf-low' },
};

const HEURISTIC_META = {
  common_input_ownership: { icon: '🔑', label: 'Common-Input Ownership' },
  shared_funding_source:  { icon: '🌊', label: 'Shared Funding Source' },
  graph_connectivity:     { icon: '🕸️', label: 'Graph Connectivity' },
};

function addrShort(addr) {
  if (!addr) return '—';
  return `${addr.slice(0, 10)}…${addr.slice(-6)}`;
}

export default function ClusteringPanel({ investigationId, investigation, result, onResultChange }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [heuristicFilter, setHeuristicFilter] = useState('all');
  const [expandedCluster, setExpandedCluster] = useState(null);
  const [expandedSection, setExpandedSection] = useState('clusters');

  const blockchain = (investigation?.blockchain || '').toLowerCase();
  const isBitcoin   = blockchain === 'bitcoin';

  const handleRun = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await investigationService.getClusters(investigationId);
      onResultChange(res.data);
      setExpandedSection('clusters');
    } catch (err) {
      console.error('Address clustering failed:', err);
      setError('Address clustering failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const clusters = result?.clusters || [];
  const summary  = result?.summary  || { total_clusters: 0, total_clustered_addresses: 0, by_heuristic: {}, btc_deep_scan: null };

  const filteredClusters = useMemo(() => {
    if (heuristicFilter === 'all') return clusters;
    return clusters.filter(c => c.heuristic === heuristicFilter);
  }, [clusters, heuristicFilter]);

  const heuristicCounts = summary.by_heuristic || {};

  const SectionHeader = ({ id, label, badge, badgeCls }) => (
    <button
      className={`cl-section-hdr${expandedSection === id ? ' active' : ''}`}
      onClick={() => setExpandedSection(expandedSection === id ? null : id)}
    >
      <span>{label}</span>
      {badge !== undefined && (
        <span className={`cl-badge ${badgeCls || ''}`}>{badge}</span>
      )}
      <span className="cl-chevron">{expandedSection === id ? '▲' : '▼'}</span>
    </button>
  );

  return (
    <div className="clustering-panel">
      <div className="cl-header">
        <h3>🧩 Address Clustering</h3>
        <p className="cl-subtitle">
          Groups multiple addresses that are likely controlled by the same real-world entity,
          using forensic heuristics ranked by confidence — not manual tagging.
        </p>
      </div>

      {isBitcoin && (
        <div className="cl-info-box cl-info-warn">
          This is a Bitcoin investigation — the high-confidence <strong>common-input-ownership</strong>
          heuristic will run, fetching raw transaction detail from Blockstream for up to 30 transactions.
          This may take a few seconds longer than other chains.
        </div>
      )}
      {!isBitcoin && (
        <div className="cl-info-box cl-info-note">
          <strong>{investigation?.blockchain?.toUpperCase() || 'This chain'}</strong> isn't Bitcoin, so the
          common-input-ownership heuristic doesn't apply (it relies on UTXO inputs, which EVM/account-based
          chains don't have). Only the shared-funding-source and graph-connectivity heuristics will run.
        </div>
      )}

      {!result && !loading && (
        <div className="cl-empty">
          <p>No clustering analysis has been run yet for this investigation.</p>
          <button className="cl-run-btn" onClick={handleRun}>▶ Run Address Clustering</button>
        </div>
      )}

      {loading && (
        <div className="cl-loading">
          <div className="cl-spinner" />
          <p>Analysing address relationships…</p>
        </div>
      )}

      {error && <div className="cl-info-box cl-info-danger">{error}</div>}

      {result && !loading && (
        <>
          {/* ── Summary strip ── */}
          <div className="cl-summary-strip">
            <div className="cl-stat">
              <div className="cl-stat-value">{summary.total_clusters}</div>
              <div className="cl-stat-label">Clusters</div>
            </div>
            <div className="cl-stat">
              <div className="cl-stat-value">{summary.total_clustered_addresses}</div>
              <div className="cl-stat-label">Addresses Clustered</div>
            </div>
            <div className="cl-stat">
              <div className="cl-stat-value">{heuristicCounts.common_input_ownership || 0}</div>
              <div className="cl-stat-label">🔑 High-Conf.</div>
            </div>
            <div className="cl-stat">
              <div className="cl-stat-value">{heuristicCounts.shared_funding_source || 0}</div>
              <div className="cl-stat-label">🌊 Med-Conf.</div>
            </div>
          </div>

          {summary.btc_deep_scan && (
            <p className="cl-scan-note">
              Bitcoin deep scan: fetched {summary.btc_deep_scan.txs_fetched} transaction(s)
              {summary.btc_deep_scan.txs_failed > 0 && `, ${summary.btc_deep_scan.txs_failed} failed`}.
            </p>
          )}

          <button className="cl-rerun-btn" onClick={handleRun} disabled={loading}>
            🔁 Re-run
          </button>

          {summary.total_clusters === 0 ? (
            <div className="cl-info-box cl-info-warn" style={{ marginTop: 12 }}>
              No clusters were detected. This usually means the graph is small, or addresses
              in this investigation don't share a funding source or (for Bitcoin) common inputs.
            </div>
          ) : (
            <>
              {/* ── Clusters ── */}
              <SectionHeader id="clusters" label="🧩 Detected Clusters" badge={filteredClusters.length} />
              {expandedSection === 'clusters' && (
                <div className="cl-section-body">
                  <div className="cl-filter-row">
                    <button
                      className={`cl-filter-chip${heuristicFilter === 'all' ? ' active' : ''}`}
                      onClick={() => setHeuristicFilter('all')}
                    >All ({clusters.length})</button>
                    {Object.entries(heuristicCounts).map(([h, count]) => {
                      const meta = HEURISTIC_META[h] || { icon: '🧩', label: h };
                      return (
                        <button
                          key={h}
                          className={`cl-filter-chip${heuristicFilter === h ? ' active' : ''}`}
                          onClick={() => setHeuristicFilter(h)}
                        >{meta.icon} {meta.label} ({count})</button>
                      );
                    })}
                  </div>

                  <div className="cl-cluster-list">
                    {filteredClusters.map((c, i) => {
                      const hMeta = HEURISTIC_META[c.heuristic] || { icon: '🧩', label: c.heuristic };
                      const cMeta = CONFIDENCE_META[c.confidence] || { label: c.confidence, cls: '' };
                      const clusterKey = `${c.heuristic}-${i}`;
                      const isOpen = expandedCluster === clusterKey;
                      return (
                        <div key={clusterKey} className="cl-cluster-row">
                          <button
                            className="cl-cluster-row-main"
                            onClick={() => setExpandedCluster(isOpen ? null : clusterKey)}
                          >
                            <span className="cl-cluster-icon">{hMeta.icon}</span>
                            <span className="cl-cluster-label">{hMeta.label}</span>
                            <span className={`cl-conf-badge ${cMeta.cls}`}>{cMeta.label}</span>
                            <span className="cl-cluster-size">{c.addresses.length} addresses</span>
                            <span className="cl-cluster-chevron">{isOpen ? '▲' : '▼'}</span>
                          </button>
                          {isOpen && (
                            <div className="cl-cluster-detail">
                              <p className="cl-cluster-note">{c.note}</p>
                              {c.shared_funder && (
                                <div className="cl-cluster-detail-row">
                                  <label>Shared funder</label>
                                  <code title={c.shared_funder}>{addrShort(c.shared_funder)}</code>
                                  <button
                                    className="cl-copy-btn"
                                    onClick={() => navigator.clipboard.writeText(c.shared_funder)}
                                    title="Copy address"
                                  >📋</button>
                                </div>
                              )}
                              <div className="cl-addr-list">
                                {c.addresses.map(addr => (
                                  <div key={addr} className="cl-addr-row">
                                    <code className="cl-addr-code" title={addr}>{addrShort(addr)}</code>
                                    <button
                                      className="cl-copy-btn"
                                      onClick={() => navigator.clipboard.writeText(addr)}
                                      title="Copy address"
                                    >📋</button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Interpretation Guide ── */}
              <SectionHeader id="guide" label="📖 Interpretation Guide" />
              {expandedSection === 'guide' && (
                <div className="cl-section-body cl-guide">
                  <div className="cl-guide-item">
                    <strong>🔑 Common-Input Ownership <span className="cl-conf-badge cl-conf-high">HIGH</span></strong>
                    <p>Bitcoin only. If two+ addresses are spent together as inputs to the same transaction,
                    a single signer must control all of them. This is the strongest clustering signal available
                    and is the basis most chain-analysis firms lead with for Bitcoin.</p>
                  </div>
                  <div className="cl-guide-item">
                    <strong>🌊 Shared Funding Source <span className="cl-conf-badge cl-conf-medium">MEDIUM</span></strong>
                    <p>Addresses that received their first inbound transaction in this investigation from the
                    same upstream wallet. Consistent with a single distributor, exchange, or operator funding
                    multiple wallets it controls — but coincidence (e.g. a popular exchange hot wallet) is possible.</p>
                  </div>
                  <div className="cl-guide-item">
                    <strong>🕸️ Graph Connectivity <span className="cl-conf-badge cl-conf-low">LOW</span></strong>
                    <p>Addresses connected by some chain of transactions, directly or indirectly. Provided as
                    context only — money moving between two wallets does not by itself imply common ownership.</p>
                  </div>
                  <div className="cl-guide-item">
                    <strong>Using clusters in an investigation</strong>
                    <p>Treat HIGH-confidence clusters as strong candidates for tagging as a single entity
                    (via the Tags panel). MEDIUM clusters warrant manual review of the shared funder before
                    drawing conclusions. LOW clusters are background context, not a finding on their own.</p>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
